import { useEffect, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment-timezone';
import Table from '../../Common/components/Table';
import useShare from '../../Share/components/hooks/useShare';

const columnHelper = createColumnHelper<any>();

interface ShareJourneyStatsProps {
    activationId: number;
}

/**
 * Classic (v1) share journey rollup: per-event engagement KPIs and a
 * per-destination table (opens, visits, photo views, downloads, reshares).
 * The v2 app renders the same data via ShareJourneySection.
 */
const PAGE = 25;

export default function ShareJourneyStats({
    activationId,
}: ShareJourneyStatsProps) {
    const { getJourneySummary, getJourneyDestinations } = useShare();
    const [summary, setSummary] = useState<any>(null);
    const [destinations, setDestinations] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        let mounted = true;
        Promise.all([
            getJourneySummary(activationId).catch(() => null),
            getJourneyDestinations(activationId, PAGE, 0).catch(() => null),
        ]).then(([s, d]) => {
            if (!mounted) return;
            setSummary(s);
            setDestinations(d?.destinations ?? []);
            setTotal(d?.total ?? 0);
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const loadMore = async () => {
        if (isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const page = await getJourneyDestinations(
                activationId,
                PAGE,
                destinations.length,
            );
            setDestinations((rows) => [
                ...rows,
                ...(page?.destinations ?? []),
            ]);
            setTotal(page?.total ?? 0);
        } catch {
        } finally {
            setIsLoadingMore(false);
        }
    };

    if (!summary || !summary.uniqueDestinations) {
        return null;
    }

    const openRate = Math.round((summary.openRate ?? 0) * 100);

    const kpis: [string, string | number][] = [
        ['Reached', summary.uniqueDestinations],
        ['Opened', `${summary.openedDestinations} (${openRate}%)`],
        ['Visits', summary.totalVisits],
        ['Photos viewed', summary.mediaViews],
        ['Downloads', summary.downloads],
        ['Reshares', summary.reshares],
        ['CTA clicks', summary.ctaClicks],
    ];

    const columns = [
        columnHelper.accessor('destination', {
            id: 'destination',
            header: () => <span>destination</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('type', {
            id: 'type',
            header: () => <span>type</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue() === 1 ? 'text' : 'email'}</span>
                </div>
            ),
        }),
        columnHelper.accessor('visits', {
            id: 'visits',
            header: () => <span>visits</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('mediaViews', {
            id: 'mediaViews',
            header: () => <span>photos viewed</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('downloads', {
            id: 'downloads',
            header: () => <span>downloads</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('reshares', {
            id: 'reshares',
            header: () => <span>reshares</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('lastSeenAt', {
            id: 'lastSeenAt',
            header: () => <span>last seen</span>,
            cell: (info) => (
                <div className="row">
                    <span>
                        {info.getValue()
                            ? moment(info.getValue()).format('MMM D, h:mm A')
                            : '—'}
                    </span>
                </div>
            ),
        }),
    ];

    return (
        <>
            <div className="pt-10 mx-auto flex max-w-10xl flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
                <h1 className="mt-1 text-xl font-semibold leading-6 text-gray-900">
                    Share Journey
                </h1>
            </div>
            <div className="mx-auto flex max-w-10xl flex-wrap gap-8 px-4 pb-4 pt-4 sm:px-6 lg:px-8">
                {kpis.map(([label, value]) => (
                    <div key={label}>
                        <div className="text-2xl font-semibold text-gray-900">
                            {typeof value === 'number'
                                ? value.toLocaleString()
                                : value}
                        </div>
                        <div className="text-sm text-gray-500">{label}</div>
                    </div>
                ))}
            </div>
            <Table columns={columns} data={destinations} />
            {total > destinations.length && (
                <div className="mx-auto flex max-w-10xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
                    <span className="text-sm text-gray-500">
                        {destinations.length} of {total}
                    </span>
                    <button
                        type="button"
                        onClick={loadMore}
                        disabled={isLoadingMore}
                        className="rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
                    >
                        {isLoadingMore ? 'Loading…' : 'Show more'}
                    </button>
                </div>
            )}
        </>
    );
}
